<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentProfileCover from 'dashboard/components-next/agent/AgentProfileCover.vue';

const getters = useStoreGetters();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentList.value;
  return picoSearch(agentList.value, query, ['name', 'email']);
});

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    const role = (agent.role || 'agent').toUpperCase();
    return t(`AGENT_MGMT.AGENT_TYPES.${role}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : t('AGENT_HUB.ROLE_CUSTOM');
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};

const openPermissions = agent => {
  if (Number(agent.id) === Number(currentUserId.value)) {
    router.push({
      name: 'profile_permissions',
      params: { accountId: route.params.accountId },
    });
    return;
  }
  router.push({
    name: 'profile_member_permissions',
    params: {
      accountId: route.params.accountId,
      userId: agent.id,
    },
  });
};

const openProfile = agent => {
  if (Number(agent.id) === Number(currentUserId.value)) {
    router.push({
      name: 'profile_settings_index',
      params: { accountId: route.params.accountId },
    });
    return;
  }
  router.push({
    name: 'profile_member_overview',
    params: {
      accountId: route.params.accountId,
      userId: agent.id,
    },
  });
};

const agentPhoto = agent =>
  agent.thumbnail || agent.avatar_url || agent.avatarUrl || '';

const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    class="w-full"
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        :search-placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="agents"
      >
        <template v-if="agentList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.COUNT', { n: agentList.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredAgentList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('AGENT_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="flex flex-wrap gap-6 border-t border-n-weak pt-6">
        <article
          v-for="agent in filteredAgentList"
          :key="agent.id || agent.email"
          class="relative w-full max-w-[22.5rem] overflow-hidden rounded-[1.85rem] bg-n-brand"
        >
          <button
            type="button"
            class="relative block w-full overflow-hidden rounded-[1.85rem] bg-[#242424] text-left"
            :aria-label="$t('AGENT_MGMT.LIST.VIEW_PROFILE')"
            @click="openProfile(agent)"
          >
            <AgentProfileCover />
            <div class="absolute right-5 top-5 max-w-[13rem] text-right">
              <p
                class="truncate text-[1.65rem] font-semibold leading-none tracking-tight text-white"
              >
                {{ getAgentRoleName(agent) }}
              </p>
              <p
                class="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/80"
              >
                {{ $t('AGENT_MGMT.LIST.ROLE') }}
              </p>
            </div>
            <div class="absolute left-5 top-14">
              <Avatar
                :src="agentPhoto(agent)"
                :name="agent.name"
                :size="96"
                rounded-full
              />
            </div>
            <div class="px-5 pb-6 pt-14">
              <h3 class="truncate text-base font-semibold text-white">
                {{ agent.name }}
              </h3>
              <p class="mt-0.5 truncate text-sm text-[#9aa0a6]">
                {{ agent.email }}
              </p>
            </div>
          </button>
          <div class="relative flex h-11 items-center justify-center px-14">
            <span
              class="flex min-w-0 items-center gap-2 text-[13px] font-medium text-white"
            >
              <span
                class="inline-flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border border-white"
              >
                <span class="i-lucide-star size-2" />
              </span>
              <span class="truncate">
                {{ $t('AGENT_MGMT.LIST.FOOTER_HINT') }}
              </span>
            </span>
            <div class="absolute right-1.5 flex items-center">
              <Button
                v-tooltip.top="$t('AGENT_MGMT.LIST.PERMISSIONS')"
                icon="i-lucide-shield"
                slate
                sm
                ghost
                class="!text-white"
                @click="openPermissions(agent)"
              />
              <Button
                v-if="showEditAction(agent)"
                v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                icon="i-woot-edit-pen"
                slate
                sm
                ghost
                class="!text-white"
                @click="openEditPopup(agent)"
              />
              <Button
                v-if="showDeleteAction(agent)"
                v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                icon="i-woot-bin"
                slate
                sm
                ghost
                class="!text-white hover:enabled:!text-n-ruby-9"
                :is-loading="loading[agent.id]"
                @click="openDeletePopup(agent)"
              />
            </div>
          </div>
        </article>
      </div>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
